import {FileTransfer} from '@capacitor/file-transfer'
import {Directory, Filesystem} from '@capacitor/filesystem'
import {Media} from '@capacitor-community/media'
import {format} from 'date-fns'
import JsZip from 'jszip'
import {useCallback, useContext, useState} from 'react'
import {useNotify} from 'react-admin'

import {ApiUrlsContext} from '../contexts/ApiUrlsContext'
import {ChatMessageFileAttachments} from '../types/graphqlSchema'
import {isNativeMobileApp} from '../utils/platform'

const useDownloadFileAttachments = (
  {onDownload}: UseDownloadFileAttachmentsProps = {}
) => {
  const notify = useNotify()
  const [isDownloading, setIsDownloading] = useState(false)
  const {getFilesApiUrl} = useContext(ApiUrlsContext)
  const downloadOnBrowser = useCallback(
    async (fileAttachments: ChatMessageFileAttachments[]) => {
      let objectUrl: string | undefined
      const a = document.createElement('a')
      if (fileAttachments.length === 1) {
        const {filename, key} = fileAttachments[0]
        a.download = filename
        a.href = getFilesApiUrl(`${key}/${filename}`)
      }
      else {
        const zip = new JsZip()
        await Promise.all(
          fileAttachments.map(({filename, key}) =>
            fetch(getFilesApiUrl(`${key}/${filename}`))
              .then(res => res.blob())
              .then(blob => zip.file(`${key}-${filename}`, blob))
          )
        )
        const blob = await zip.generateAsync({type: 'blob'})
        a.download = 'attachments.zip'
        objectUrl = URL.createObjectURL(blob)
        a.href = objectUrl
      }
      a.style.display = 'none'
      document.body.append(a)
      a.click()
      a.remove()
      setTimeout(() => {
        objectUrl && URL.revokeObjectURL(objectUrl)
      }, 40_000)
    },
    [getFilesApiUrl],
  )
  const downloadOnNativeMobile = useCallback(
    async (fileAttachments: ChatMessageFileAttachments[]) => {
      const album = (
        (await Media.getAlbums()).albums.find(a => a.name === 'Flinkit') ??
        await Media.createAlbum({name: 'Flinkit'})
          .then(() => Media.getAlbums())
          .then(({albums}) => albums.find(a => a.name === 'Flinkit'))
      )
      await Promise.all(
        fileAttachments.map(async file => {
          const filename = `${format(new Date(), 'yyyyMMdd_HHmmss')}_${file.filename}`
          const url = getFilesApiUrl(`${file.key}/${file.filename}`)
          if (file.mimeType.startsWith('image/')) {
            await Media.savePhoto({
              albumIdentifier: album?.identifier,
              fileName: filename.replace(/\.[^.]*$/, ''),
              path: url,
            })
            return
          }
          if (file.mimeType.startsWith('video/')) {
            await Media.saveVideo({
              albumIdentifier: album?.identifier,
              fileName: filename.replace(/\.[^.]*$/, ''),
              path: url,
            })
            return
          }
          const filesystemPath = await Filesystem.getUri({
            directory: Directory.Documents,
            path: filename,
          })
          await FileTransfer.downloadFile({path: filesystemPath.uri, url})
        })
      )
    },
    [getFilesApiUrl],
  )
  const download = useCallback(
    async (fileAttachments: ChatMessageFileAttachments[]) => {
      try {
        setIsDownloading(true)
        isNativeMobileApp ?
          await downloadOnNativeMobile(fileAttachments) :
          await downloadOnBrowser(fileAttachments)
        notify(
          'ra.notification.files_downloaded',
          {messageArgs: {smart_count: fileAttachments.length}}
        )
        onDownload?.()
      }
      finally {
        setIsDownloading(false)
      }
    },
    [
      downloadOnBrowser,
      downloadOnNativeMobile,
      notify,
      onDownload,
    ]
  )
  return {download, isDownloading}
}

type UseDownloadFileAttachmentsProps = {
  onDownload?: VoidFunction
}

export default useDownloadFileAttachments
